import { useTranslation } from 'react-i18next';
import { Reveal, RevealGroup, RevealItem } from './Reveal';

const TESTIMONIAL_KEYS = [
  'freelancer',
  'projectManager',
  'student',
  'salesPerson',
  'contentCreator',
] as const;

// Masonry-style layout: col 1 gets items 0,2,4 · col 2 gets items 1,3
const COL_A = [0, 2, 4] as const;
const COL_B = [1, 3] as const;

export default function Testimonials() {
  const { t } = useTranslation();

  return (
    <section id="testimonials" className="py-24 md:py-32 bg-[#f0eee6] dark:bg-[#1a1918]">
      <div className="mx-auto max-w-6xl px-6">
        {/* Section header */}
        <Reveal className="mb-16 max-w-2xl">
          <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-[#1414131a] dark:border-[#faf9f51a] px-4 py-1.5 text-[#6b6a68] dark:text-[#9a9893]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ae5630]" />
            {t('testimonials.title')}
          </span>
          <h2 className="font-display display-tight mt-5 text-3xl font-medium leading-[1.1] text-[#141413] dark:text-[#faf9f5] md:text-5xl">
            {t('testimonials.subtitle')}
          </h2>
        </Reveal>

        {/* Masonry two-column grid (desktop) / single column (mobile) */}
        <div className="hidden md:grid md:grid-cols-2 md:gap-5">
          {/* Column A */}
          <RevealGroup className="flex flex-col gap-5">
            {COL_A.map((idx) => {
              const key = TESTIMONIAL_KEYS[idx];
              return (
                <RevealItem key={key}>
                  <TestimonialCard testimonialKey={key} />
                </RevealItem>
              );
            })}
          </RevealGroup>

          {/* Column B — offset to break symmetry */}
          <RevealGroup className="flex flex-col gap-5 md:mt-10">
            {COL_B.map((idx) => {
              const key = TESTIMONIAL_KEYS[idx];
              return (
                <RevealItem key={key}>
                  <TestimonialCard testimonialKey={key} />
                </RevealItem>
              );
            })}
          </RevealGroup>
        </div>

        {/* Mobile: single column */}
        <RevealGroup className="flex flex-col gap-4 md:hidden">
          {TESTIMONIAL_KEYS.map((key) => (
            <RevealItem key={key}>
              <TestimonialCard testimonialKey={key} />
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
}

function TestimonialCard({ testimonialKey }: { testimonialKey: typeof TESTIMONIAL_KEYS[number] }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-2xl border border-[#1414131a] dark:border-[#faf9f51a] bg-[#fffefb] dark:bg-[#1a1918] p-6 shadow-warm transition-all duration-300 ease-spring hover:shadow-warm-lg hover:-translate-y-0.5">
      {/* Large opening quote mark */}
      <span
        className="font-display block text-5xl leading-none text-[#ae5630]/30 select-none mb-2"
        aria-hidden="true"
      >
        &ldquo;
      </span>

      <p className="text-[#6b6a68] dark:text-[#9a9893] leading-relaxed text-[15px]">
        {t(`testimonials.${testimonialKey}.quote`)}
      </p>

      <div className="mt-6 flex items-center gap-3 border-t border-[#1414131a] dark:border-[#faf9f51a] pt-5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#ae5630]/10 dark:bg-[#ae5630]/20 text-[#ae5630] text-sm font-semibold">
          {t(`testimonials.${testimonialKey}.avatar`)}
        </div>
        <div>
          <div className="text-sm font-semibold text-[#141413] dark:text-[#faf9f5]">
            {t(`testimonials.${testimonialKey}.name`)}
          </div>
          <div className="text-xs text-[#87867f]">
            {t(`testimonials.${testimonialKey}.role`)}
          </div>
        </div>
      </div>
    </div>
  );
}
